import { type ApiKeyContent, ApiKeyRequester } from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getApiKeyRequester = () => new ApiKeyRequester({ host: env.cerberusHost });

const QUERY_KEY = 'API_KEYS';

const useGetApiKeys = () =>
  useQuery<ApiKeyContent[]>({
    queryKey: [QUERY_KEY, 'GET_API_KEYS'],
    queryFn: () => getApiKeyRequester().getApiKeys(),
  });

const useInvalidateApiKeys = (): (() => void) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'GET_API_KEYS'] });
};

const useGenerateNewApiKey = () =>
  useMutation<string, unknown, { apiKeyName: string }>({
    mutationFn: (variables) => getApiKeyRequester().generateNewApiKey(variables.apiKeyName),
  });

const useRevokeApiKey = () =>
  useMutation<void, unknown, { apiKeyPrefix: string }>({
    mutationFn: (variables) => getApiKeyRequester().revokeApiKey(variables.apiKeyPrefix),
  });

export { useGetApiKeys, useGenerateNewApiKey, useRevokeApiKey, useInvalidateApiKeys };
